import React, { useEffect, useState } from 'react';
import axiosInstance from 'src/axiosInstance';
import { BallTriangle } from 'react-loader-spinner';
import { Modal, MenuItem, Select, TextField } from '@mui/material';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import NoRecordFound from 'src/components/NoRecordFound';
import { useAuth } from 'src/hooks/useAuth';
import { useNavigate } from 'react-router-dom';

const FIELDS = [
  { label: 'S/N', key: 'sn' },
  { label: 'Customer Name', key: 'customerName' },
  { label: 'Product Purchased', key: 'productUnit' },
  { label: 'Total Discount', key: 'totalDiscount' },
  { label: 'Payment Type', key: 'paymentType' },
  { label: 'Order ID', key: 'orderId' },
  { label: 'Total Price', key: 'price' },
  { label: 'Date Purchased', key: 'created_at' },
  { label: 'Patronage', key: 'patronage' },
];

const getNumber = (val) => {
  if (typeof val === 'object' && val !== null && '$numberDecimal' in val) {
    return Number(val.$numberDecimal);
  }
  return Number(val) || 0;
};

const MobileSales = () => {
  // All hooks at the top
  const { data: user } = useAuth();
  const navigate = useNavigate();
  const [testModeModal, setTestModeModal] = useState(false);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debounceTimer, setDebounceTimer] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    min_price: '',
    max_price: '',
    min_patronage: '',
    max_patronage: '',
    payment_type: '',
    status: '',
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    if (user && !user.onboarded) setTestModeModal(true);
  }, [user]);

  useEffect(() => {
    fetchSales(page, searchTerm, filters);
    // eslint-disable-next-line
  }, [page]);

  // Debounced search
  useEffect(() => {
    if (debounceTimer) clearTimeout(debounceTimer);
    const timer = setTimeout(() => {
      setPage(1);
      fetchSales(1, searchTerm, filters);
    }, 400);
    setDebounceTimer(timer);
    // eslint-disable-next-line
  }, [searchTerm]);

  const fetchSales = async (pageNum = 1, term = "", filterVals = {}) => {
    setLoading(true);
    try {
      const params = {
        page: pageNum,
        pageSize: 10,
        search: term,
      };
      if (filterVals.min_price) params.min_price = filterVals.min_price;
      if (filterVals.max_price) params.max_price = filterVals.max_price;
      if (filterVals.min_patronage) params.min_patronage = filterVals.min_patronage;
      if (filterVals.max_patronage) params.max_patronage = filterVals.max_patronage;
      if (filterVals.payment_type) params.payment_method = filterVals.payment_type;
      if (filterVals.status) params.status = filterVals.status;
      if (filterVals.startDate) params.dateFrom = filterVals.startDate;
      if (filterVals.endDate) params.dateTo = filterVals.endDate;
      const res = await axiosInstance.get('/shop/sale/get', { params });
      setSales(res.data.data.data || []);
      setPagination(res.data.data.pagination);
    } catch (err) {
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setFilterOpen(false);
    setPage(1);
    fetchSales(1, searchTerm, filters);
  };

  const handleResetFilters = () => {
    setFilters({
      min_price: '',
      max_price: '',
      min_patronage: '',
      max_patronage: '',
      payment_type: '',
      status: '',
      startDate: null,
      endDate: null,
    });
  };

  if (user && !user.onboarded) {
    return (
      <Modal open={testModeModal} onClose={() => {}}>
        <div style={{
          background: 'white',
          borderRadius: 20,
          maxWidth: 350,
          margin: '100px auto',
          padding: 32,
          boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none',
        }}>
          <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Currently on Test Mode</h2>
          <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            Complete your profile to perform other transactions
          </p>
          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button
              style={{
                flex: 1,
                background: 'white',
                color: '#af3585',
                border: '2px solid #af3585',
                borderRadius: 8,
                padding: '10px 8px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginRight: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                background: '#af3585',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 7px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginLeft: 4
              }}
              onClick={() => navigate('/app/profile?edit=1')}
            >
              Complete Profile
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  // Awaiting Approval modal logic
  if (user && user.onboarded && (user.is_approved !== 'verified' || !user.is_verified)) {
    return (
      <Modal open={true} onClose={() => {}}>
        <div style={{
          background: 'white',
          borderRadius: 20,
          maxWidth: 350,
          margin: '100px auto',
          padding: 32,
          boxShadow: '0 4px 32px rgba(0,0,0,0.15)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          outline: 'none',
        }}>
          <div style={{ fontSize: 48, color: '#f79009', marginBottom: 16 }}>⚠️</div>
          <h2 style={{ fontWeight: 700, fontSize: 22, margin: 0, marginBottom: 8, textAlign: 'center' }}>Awaiting Verification</h2>
          <p style={{ color: '#475467', fontSize: 16, textAlign: 'center', marginBottom: 24 }}>
            Your profile is currently being verified. This will take at least 24 hours.
          </p>
          <div style={{ display: 'flex', gap: 16, width: '100%' }}>
            <button
              style={{
                flex: 1,
                background: 'white',
                color: '#af3585',
                border: '2px solid #af3585',
                borderRadius: 8,
                padding: '10px 8px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                minWidth: 0,
                marginRight: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Cancel
            </button>
            <button
              style={{
                flex: 1,
                background: '#af3585',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                padding: '10px 7px',
                fontWeight: 500,
                fontSize: 14,
                cursor: 'pointer',
                marginLeft: 4
              }}
              onClick={() => navigate('/app/dashboard')}
            >
              Back Home
            </button>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <div style={{ background: '#fff', minHeight: '100vh', padding: '0 0 80px 0' }}>
      <div style={{ padding: '18px 0 0 0', background: '#fff', borderBottom: '1px solid #eee', position: 'sticky', top: 0, zIndex: 2 }}>
        <span style={{ fontWeight: 700, fontSize: 23, color: '#222', marginLeft: 18 }}>Sales</span>
        <div style={{ color: '#757575', fontSize: 14, marginLeft: 18, marginTop: 4, marginBottom: 10 }}>
          Keep track of your sales
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', margin: '14px 12px 0 12px' }}>
        <div
          style={{
            flex: 1,
            background: '#fff',
            border: '1.5px solid #E0E0E0',
            borderRadius: 10,
            display: 'flex',
            alignItems: 'center',
            padding: '0 10px',
            height: 38,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="7" stroke="#757575" strokeWidth="2" strokeLinecap="round" />
            <path d="M20 20l-3-3" stroke="#757575" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search"
            style={{
              border: 'none',
              outline: 'none',
              fontSize: 15,
              marginLeft: 8,
              width: '100%',
              color: '#757575',
              background: 'transparent',
            }}
          />
        </div>
        <button
          onClick={() => setFilterOpen(true)}
          style={{
            marginLeft: 10,
            padding: 8,
            border: '1.5px solid #E0E0E0',
            borderRadius: 10,
            background: '#fff',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 38,
            width: 38,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M4 6h16M7 12h10M10 18h4" stroke="#222" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <Modal open={filterOpen} onClose={() => setFilterOpen(false)}>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: '#fff',
          borderRadius: 12,
          boxShadow: '0 2px 16px rgba(0,0,0,0.12)',
          padding: 24,
          minWidth: 280,
          maxWidth: '90vw',
        }}>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 12 }}>Filter</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 14, color: '#757575' }}>Status</label>
              <Select
                fullWidth
                size="small"
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                displayEmpty
                style={{ marginTop: 4 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="abandoned">Abandoned</MenuItem>
              </Select>
            </div>
            <div>
              <label style={{ fontSize: 14, color: '#757575' }}>Payment Type</label>
              <Select
                fullWidth
                size="small"
                value={filters.payment_type}
                onChange={e => handleFilterChange('payment_type', e.target.value)}
                displayEmpty
                style={{ marginTop: 4 }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="transfer">Transfer</MenuItem>
                <MenuItem value="wallet">Wallet</MenuItem>
                <MenuItem value="card">Card</MenuItem>
              </Select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Min Price</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.min_price}
                  onChange={e => handleFilterChange('min_price', e.target.value)}
                  placeholder="Min"
                  inputProps={{ min: 0 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Max Price</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.max_price}
                  onChange={e => handleFilterChange('max_price', e.target.value)}
                  placeholder="Max"
                  inputProps={{ min: 0 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Min Patronage</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.min_patronage}
                  onChange={e => handleFilterChange('min_patronage', e.target.value)}
                  placeholder="Min"
                  inputProps={{ min: 0 }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Max Patronage</label>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={filters.max_patronage}
                  onChange={e => handleFilterChange('max_patronage', e.target.value)}
                  placeholder="Max"
                  inputProps={{ min: 0 }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>Start Date</label>
                <DatePicker
                  selected={filters.startDate}
                  onChange={date => handleFilterChange('startDate', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Start"
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ fontSize: 14, color: '#757575' }}>End Date</label>
                <DatePicker
                  selected={filters.endDate}
                  onChange={date => handleFilterChange('endDate', date)}
                  dateFormat="dd/MM/yyyy"
                  placeholderText="End"
                  className="form-control"
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 10 }}>
              <button onClick={handleApplyFilters} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#af3585', color: '#fff', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>Apply</button>
              <button onClick={handleResetFilters} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1px solid #E0E0E0', background: '#fff', color: '#af3585', fontWeight: 700, fontSize: 16, cursor: 'pointer' }}>Reset</button>
            </div>
          </div>
        </div>
      </Modal>
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '40vh' }}>
          <BallTriangle height={50} width={55} radius={5} color="#af3585" ariaLabel="ball-triangle-loading" visible />
        </div>
      ) : sales.length === 0 ? (
        <NoRecordFound message="No sales found." />
      ) : (
        <div style={{ margin: '18px 0 0 0' }}>
          {sales.map((sale, idx) => (
            <div key={sale._id || idx} style={{
              background: '#faf9fb',
              borderRadius: 14,
              boxShadow: '0 2px 8px rgba(175,53,133,0.04)',
              padding: '16px 18px',
              margin: '0 14px 18px 14px',
              fontSize: 15,
              border: '1px solid #f1e6f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ color: '#af3585', fontWeight: 700 }}>S/N</span>
                <span style={{ fontWeight: 700, color: '#222' }}>{(pagination?.pageSize || 10) * (page - 1) + idx + 1}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Customer Name</span>
                <span style={{ color: '#222', fontWeight: 500 }}>{sale.customerName || sale.customer_name || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Product Purchased</span>
                <span style={{ color: '#222' }}>{sale.productUnit || sale.product_purchased || sale.product_unit || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Total Discount</span>
                <span style={{ color: '#af3585', fontWeight: 600 }}>₦{getNumber(sale.totalDiscount || sale.total_discount).toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Payment Type</span>
                <span style={{ color: '#222' }}>{sale.paymentType || sale.payment_type || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Order ID</span>
                <span style={{ color: '#af3585', fontWeight: 700 }}>{sale.orderId || sale.order_id || '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Total Price</span>
                <span style={{ color: '#12B76A', fontWeight: 700 }}>₦{getNumber(sale.price || sale.total_price).toLocaleString()}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Date Purchased</span>
                <span style={{ color: '#222' }}>{sale.created_at ? new Date(sale.created_at).toLocaleDateString() : '-'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#757575', fontWeight: 600 }}>Patronage</span>
                <span style={{ color: '#222' }}>{sale.patronage || '-'}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    
    </div>
  );
};

export default MobileSales; 